import React, { useEffect, useMemo, useState } from 'react';
import { Alert, notification } from 'antd';
import RequestFormLayout from 'components/layout/RequestFormLayout';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import useGuardingFlow from 'core/guardingRequestForm/useGuardingFlow';
import DateSelector from 'components/Form/DateSelector';
import ShiftSelector from 'components/Form/ShiftSelector';
import CostCode from 'components/Form/CostCode';
import type { SaveShiftValues } from 'components/Form/ShiftSelector/types';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { applyRetail247Option, buildRetail247Blocks, buildShiftOptions, getRetailDayFilter, orderRetailShiftOptions, resolveRetailShiftForDate } from 'core/utils/retailDateHelpers';
import collapseSingleBlockDates from 'core/utils/shiftBlocks';
import { deleteGuardingRequest } from 'core/guardingRequestForm/guardingRequestFormApi';
import { Shift, VisitRequestFormResponsePayload } from 'core/guardingRequestForm/types';
import { isEmpty } from 'underscore';
import moment from 'moment';
import { unwrapResult } from '@reduxjs/toolkit';
import { shiftIsOnCalendarDate, usToIso } from 'core/utils/date';
import { isCostApprovalExemptEmail } from 'core/utils/value';
import { GUARDING_STEPPER_LABELS, GUARDING_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import { BankHolidayMap, bankHolidayDates, fetchBankHolidays, toCalendarHolidays } from 'core/utils/bankHolidays';
import { BankHolidayModalState, bankHolidayChargeDates } from 'core/utils/bankHolidayWarning';
import BankHolidayWarningModal from 'components/Modals/BankHolidayWarningModal';
import { HandleDateValues } from './types';

export interface VisitDatesProps {
    id: number | null;
    date: string,
    saved: boolean,
    shiftName?: string | undefined,
    startTime?: string | undefined,
    endTime?: string | undefined,
    range?: boolean | undefined,
    startDate?: string | undefined,
    endDate?: string | undefined,
    shiftType?: number | string | undefined,
    blockDate?: string | undefined | null,
}

interface TimeSlot {
    label: string;
    startTime: string;
    endTime: string;
    day?: number | null;
}

interface ShiftOption {
    id?: number;
    name: string;
    label?: string;
    value: string;
    timeSlots: TimeSlot[];
}

interface ShiftSelected {
    _tempIndex?: number | undefined;
    blockDate?: string | undefined | null;
    id?: number | null;
    day?: string;
    date: string;
    shift: string | undefined;
    shiftType: number | string | undefined;
    startTime: string | undefined;
    endTime: string | undefined;
    bookingTimeCritical?: boolean;
    saved?: boolean;
}

interface customShiftProps {
    startTime: string;
    endTime: string;
    date: string;
    blockDate: string | undefined | null;
}

const MOBILE_RESPONSE_MAX_SHIFT_HOURS = 3;

const GuardingDateSelectionForm: React.FC = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const dispatch = useDispatch<AppDispatch>();
    const { id } = useParams<{ id: string }>();
    const flow = useGuardingFlow();
    const { stepData, visitRequest: visitRequestState, isFetching, isUpdating } = useSelector((state: CombinedStates) => state.guardingRequestForm);
    const [selectedDates, setSelectedDates] = useState<Array<string>>([]);
    const [visitDates, setVisitDates] = useState<VisitDatesProps[]>([]);
    const [shiftOptions, setShiftOptions] = useState<ShiftOption[]>([]);
    const [selectedShift, setSelectedShift] = useState<ShiftSelected[]>([]);
    // A bank holiday suspends the site's open window, so a shift on one is
    // charged where it would normally be free — the calendar toggle and the
    // billed-hours figure both read this.
    const [bankHolidays, setBankHolidays] = useState<BankHolidayMap>({});
    const holidays = useMemo(() => toCalendarHolidays(bankHolidays), [bankHolidays]);
    const bankHolidayList = useMemo(() => bankHolidayDates(bankHolidays), [bankHolidays]);
    const [bankHolidayAcknowledged, setBankHolidayAcknowledged] = useState<boolean>(false);
    const [bankHolidayModal, setBankHolidayModal] = useState<BankHolidayModalState>({
        open: false,
        navigationState: null,
    });
    const [costCentre, setCostCentre] = useState<string>('');
    const [costApprovalEmail, setCostApprovalEmail] = useState<string>('');
    const [requireEmail, setRequireEmail] = useState<boolean>(false);
    const minimumNoticeDays = stepData?.minimumNoticeDays || 0;
    const minimumCalendarDate = minimumNoticeDays > 0
        ? moment().add(minimumNoticeDays, 'days').format('YYYY-MM-DD')
        : moment().format('YYYY-MM-DD');
    const isMobileResponse = stepData?.isMobileResponse || false;
    const maxShiftHours = isMobileResponse ? MOBILE_RESPONSE_MAX_SHIFT_HOURS : 0;
    const isRetail = stepData?.isRetail || false;
    // The step's single billed-hours figure, seeded from the saved request and
    // then owned by the shift selector, which recalculates it whenever the
    // shifts change. Both the selector's "Total Hours Billed" line and the cost
    // code read this one value, so the two can't drift apart.
    const [billedHours, setBilledHours] = useState<number>(stepData?.totalHours ?? 0);
    const openDaysByDay = isRetail ? stepData?.openDaysByDay : undefined;
    const amending = navState?.amending;
    // Auto-cleanup request if user leaves without completing
    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequestState.encryptedId : null,
        formBasePath: '/guarding-request/',
        deleteEndpoint: 'guarding-request',
        dispatchDelete: () => {
            if (visitRequestState.encryptedId) dispatch(deleteGuardingRequest(visitRequestState.encryptedId));
        },
    });

    useEffect(() => {
        fetchBankHolidays().then(setBankHolidays);
    }, []);

    // The saved shifts that a bank holiday turns from free into chargeable.
    const bankHolidayCharges = useMemo(() => bankHolidayChargeDates(
        visitDates.filter((date) => date.saved && date.startTime && date.endTime),
        {
            enabled24_7: stepData?.enabled24_7 ?? false,
            openDays: stepData?.openDays,
            isGuardingRequest: true,
        },
        bankHolidays,
    ), [visitDates, bankHolidays, stepData?.enabled24_7, stepData?.openDays]);

    // Warn again if the affected dates change — adding another bank holiday, or
    // editing one of these shifts, is a fresh charge to be told about. Changing
    // the cost code is not.
    const bankHolidayKey = bankHolidayCharges.map((charge) => charge.date).join('|');
    useEffect(() => {
        setBankHolidayAcknowledged(false);
    }, [bankHolidayKey]);

    // Whether to ask for a cost approver, matching the visitor request rule:
    // a requester whose own address is on an exempt domain (Vodafone / Mitie)
    // is not asked, nor is anyone flagged as bypassing cost approval, nor a
    // booking with nothing billable to sign off.
    useEffect(() => {
        if (visitRequestState.costApproverByPass || billedHours <= 0) {
            setRequireEmail(false);
        } else if (visitRequestState?.requester?.email) {
            setRequireEmail(!isCostApprovalExemptEmail(visitRequestState.requester.email));
        }
    }, [billedHours, visitRequestState.costApproverByPass, visitRequestState?.requester?.email]);

    const handleNextClick = (navigationState: Record<string, unknown>, skipBankHolidayCheck = false) => {
        if (visitDates.length === 0) {
            notification.error({
                message: 'No Dates Selected',
                description: 'Please select at least one date to proceed',
            });
            return;
        }

        if (isEmpty(selectedShift)) {
            notification.error({
                message: 'Shift Not Selected',
                description: 'Please select a shift to proceed',
            });
            return;
        }

        if (visitDates.some((date) => !date.saved)) {
            notification.error({
                message: 'Shift Not Saved',
                description: 'Please save the shift to proceed',
            });
            return;
        }

        // Ahead of the cost-code check below: a bank holiday is why a booking
        // that cost nothing yesterday now needs a cost code, and "Cost Code Not
        // Provided" on its own explains none of that.
        if (!skipBankHolidayCheck && bankHolidayCharges.length > 0 && !bankHolidayAcknowledged) {
            setBankHolidayModal({ open: true, navigationState });
            return;
        }

        // Guarding always requires a cost code (retail included) — only Retail
        // General Access is billed at N/A.
        const submittedCostCentre = costCentre.trim();

        if (submittedCostCentre === '') {
            notification.error({
                message: 'Cost Code Not Provided',
                description: 'Please fill a cost code to proceed',
            });
            return;
        }

        const submittedCostApprovalEmail = costApprovalEmail.trim();

        if (requireEmail && submittedCostApprovalEmail === '') {
            notification.error({
                message: 'Email Not Provided',
                description: 'Please fill an email to proceed',
            });
            return;
        }

        if (requireEmail && !isCostApprovalExemptEmail(submittedCostApprovalEmail)) {
            notification.error({
                message: 'Invalid Email',
                description: 'Please enter a valid Vodafone email to proceed',
            });
            return;
        }
        if (selectedDates !== null && (id !== undefined || flow.guestMode) && !isFetching && !isUpdating) {
            flow.updateStep(2, {
                selectedDates: selectedShift,
                costCode: {
                    costCentre: submittedCostCentre,
                    // Only sent when the requester was actually asked for
                    // one: an exempt-domain requester has no approver to
                    // name, and a request with no approver skips cost
                    // approval rather than stalling with nobody to sign it.
                    costApprovalEmail: requireEmail ? submittedCostApprovalEmail : '',
                },
                amending,
                forcedStep: 2,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    flow.goToStep(3, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(1, navigationState);
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            const resultAction = await flow.loadStep(2, amending);

            // Redirect to dashboard if guarding request not found
            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Guarding Request Not Found',
                    description: 'The guarding request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Guarding Request',
                description: 'Please select a guarding request to proceed',
            });
        }
    };

    const formatDate = (dateString: string) => {
        // Parse explicitly: inputs are MM-DD-YYYY (form state) or YYYY-MM-DD /
        // ISO (API). new Date("MM-DD-YYYY") returns Invalid Date in Safari,
        // which surfaced as "Invalid date" on the calendar.
        const parsed = moment(dateString, ['MM-DD-YYYY', 'YYYY-MM-DD', moment.ISO_8601], true);
        return parsed.isValid() ? parsed.format('MM-DD-YYYY') : dateString;
    };

    useEffect(() => {
        handleInitialLoad();
    }, [id, amending]);

    const handleDatesSelect = (values: HandleDateValues) => {
        if (values.dates && !values.dates.includes('Invalid date')) {
            let validDate = values.dates;

            if (!values.includeWeekends) {
                validDate = validDate.filter((date) => {
                    const day = new Date(date).getDay();
                    return day !== 0 && day !== 6;
                });
            }

            if (!values.includeBankHolidays) {
                validDate = validDate.filter((date) => !holidays.includes(date));
            }

            const nextSelectedDates = validDate.map(date => formatDate(date));
            setSelectedDates(nextSelectedDates);

            const formatted = validDate.concat(visitRequestState.dates?.map((date) => {
                const parts = date.date.split('-');
                const formattedDate = `${parts[1]}-${parts[2]}-${parts[0]}`;
                return formattedDate;
            }).filter((formattedDate) => !validDate.includes(formattedDate)) || []);

            validDate = formatted?.filter((date) => validDate.includes(date));
            const filteredOutDate = nextSelectedDates.filter((date) => !validDate.includes(date))
                .concat(formatted?.filter((date) => !validDate.includes(date)) || []);

            const formattedFilteredOut = filteredOutDate.map((date) => {
                const parts = date.split('-');
                return `${parts[2]}-${parts[0]}-${parts[1]}`;
            });

            setSelectedShift((prevSelectedShifts) => prevSelectedShifts.filter((shift) => !formattedFilteredOut.includes(shift.date)));

            const sortedDates = validDate.sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());
            const selectedVisitDates = sortedDates.map((dates) => {
                const date = formatDate(dates);
                const parts = date.split('-');
                const formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;
                const dateId = selectedShift?.find((visitDate) => visitDate.date === dates)?.id || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.id || null;
                const saved = selectedShift?.some((visitDate) => visitDate.date === dates) || selectedShift?.some((visitDate) => visitDate.date === formattedDate) || false;
                const shift = selectedShift?.find((visitDate) => visitDate.date === dates)?.shift || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.shift;
                const startTime = selectedShift?.find((visitDate) => visitDate.date === dates)?.startTime || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.startTime;
                const endTime = selectedShift?.find((visitDate) => visitDate.date === dates)?.endTime || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.endTime;
                const shiftType = selectedShift?.find((visitDate) => visitDate.date === dates)?.shiftType || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.shiftType;
                const blockDate = selectedShift?.find((visitDate) => visitDate.date === dates)?.blockDate || selectedShift?.find((visitDate) => visitDate.date === formattedDate)?.blockDate;
                return {
                    id: dateId,
                    date,
                    saved,
                    shiftName: shift,
                    shiftType,
                    startTime,
                    endTime,
                    blockDate,
                };
            });

            setVisitDates(prevDate => {
                const filteredSelectedVisitDates = selectedVisitDates.filter(selectedItem => !prevDate.some(prevItem => prevItem.date === selectedItem.date));
                const filteredPrevDate = prevDate.filter(prevItem => sortedDates.includes(prevItem.date));
                const mergedVisitDates = [...filteredPrevDate, ...filteredSelectedVisitDates];

                const sortedMergeDates = mergedVisitDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

                const uniqueVisitDates = sortedMergeDates.reduce((unique: VisitDatesProps[], item) => {
                    const isNullId = item.id === null;
                    const isDuplicate = !isNullId && unique.some(existingItem => existingItem.id === item.id);
                    if (isNullId || !isDuplicate) {
                        unique.push(item);
                    }
                    return unique;
                }, []);

                // Retail guarding takes every day, closed ones included: a
                // closed day borrows the previous open day's hours, so there
                // is nothing to drop here (General Visit keeps its own check).
                return uniqueVisitDates;
            });

        }
    };

    function checkForOverlap(newShift: customShiftProps, existingShifts: VisitDatesProps[] | customShiftProps[]): boolean {
        const format = 'MM-DD-YYYY HH:mm:ss';
        const newStart = moment(`${newShift.date} ${newShift.startTime}`, format);
        const newEnd = moment(`${newShift.date} ${newShift.endTime}`, format);

        if (newEnd.isBefore(newStart)) {
            newEnd.add(1, 'day');
        }

        return existingShifts.some(shift => {
            if (shift.startTime === undefined || shift.endTime === undefined) {
                return false;
            }
            const existingStart = moment(`${shift.date} ${shift.startTime}`, format);
            const existingEnd = moment(`${shift.date} ${shift.endTime}`, format);

            if (existingEnd.isBefore(existingStart)) {
                existingEnd.add(1, 'day');
            }

            return newStart.isBefore(existingEnd) && newEnd.isAfter(existingStart);
        });
    }

    const handleSaveShift = (values: SaveShiftValues) => {
        const shifts: customShiftProps[] = [];
        const validDates = [...values.dates];

        // Parse explicitly: new Date("MM-DD-YYYY") is Invalid Date in Safari,
        // which makes getTime() NaN and corrupts the sort order. Deduped: a
        // date holding two 24/7 blocks arrives once per row, and the retail
        // cover start must apply to its first day exactly once.
        const sortedInputDates = [...new Set(validDates)].sort((a, b) => moment(a, 'MM-DD-YYYY').valueOf() - moment(b, 'MM-DD-YYYY').valueOf());
        const retailSkippedDates: string[] = [];

        // Retail 24/7 runs on the store's own clock: open → night start, then
        // night start → next day's opening, with a closed day borrowing the
        // previous open day's hours. An optional start time moves where the
        // cover begins on its first day (Sat 16:30 → Sun 09:00, then Sunday's
        // own blocks, through to Monday's opening).
        const isRetail247 = isRetail && values.shift === '24/7';
        // Day/Night on retail: times come from the site's per-weekday shift row,
        // so nothing is picked and the save arrives with no start time.
        const isRetailResolved = isRetail && !isRetail247 && values.shift !== 'Custom';
        if (isRetail247) {
            const coverStart = values.customTimeSlotStartTime ? `${values.customTimeSlotStartTime}:00` : null;
            sortedInputDates.forEach((date, index) => {
                const blocks = buildRetail247Blocks(date, openDaysByDay, index === 0 ? coverStart : null);
                if (!blocks) {
                    retailSkippedDates.push(moment(date, 'MM-DD-YYYY').format('DD/MM/YYYY'));
                    return;
                }
                shifts.push(...blocks);
            });
        }
        // Custom shift logic
        if (!isRetail247 && values.shift === '24/7' && values.startTime === values.endTime && values.timeSlot !== 'Custom') {
            sortedInputDates.forEach((date) => {
                // ISO form (usToIso + "T"): new Date("MM-DD-YYYY HH:mm") is
                // Invalid Date in Safari, which moment then renders as the
                // literal "Invalid date" into the saved shift.
                const visitStart = `${usToIso(date)}T${values.startTime}`;
                const visitEnd = `${usToIso(date)}T${values.endTime}`;
                const initialEndTime = new Date(visitStart);
                initialEndTime.setHours(initialEndTime.getHours() + 12);
                shifts.push({
                    startTime: moment(new Date(visitStart)).format('HH:mm:ss'),
                    endTime: moment(new Date(initialEndTime)).format('HH:mm:ss'),
                    date: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                    blockDate: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                });
                shifts.push({
                    startTime: moment(new Date(initialEndTime)).format('HH:mm:ss'),
                    endTime: moment(new Date(visitEnd)).format('HH:mm:ss'),
                    date: moment(initialEndTime).format('MM-DD-YYYY'),
                    blockDate: moment(new Date(visitStart)).format('MM-DD-YYYY'),
                });
            });
        }
        if (values.timeSlot === 'Custom') {
            sortedInputDates.forEach((date) => {

                const visitStart = `${usToIso(date)}T${values.customTimeSlotStartTime}`;
                const initialEndTime = new Date(visitStart);
                const startCustom = new Date(visitStart);
                let isExactStart = false;

                if ((startCustom.getHours() === 7 || startCustom.getHours() === 19) && startCustom.getMinutes() === 0) {
                    isExactStart = true;
                    const firstShiftEndTime = new Date(startCustom);
                    firstShiftEndTime.setHours(startCustom.getHours() + 12);
                    shifts.push({
                        startTime: moment(new Date(startCustom)).format('HH:mm:ss'),
                        endTime: moment(firstShiftEndTime).format('HH:mm:ss'),
                        date: moment(startCustom).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    });
                }

                let nextStartTime: Date;
                if (isExactStart) {
                    nextStartTime = new Date(`${usToIso(date)}T${shifts[shifts.length - 1]?.endTime}`) || new Date(startCustom);
                } else {
                    if (startCustom.getHours() < 19) {
                        if (startCustom.getHours() < 19 && startCustom.getHours() >= 7) {
                            initialEndTime.setHours(19, 0, 0, 0); // Set to 7pm same day
                        } else {
                            initialEndTime.setHours(7, 0, 0, 0); // Same day 7am
                        }
                    } else {
                        initialEndTime.setDate(initialEndTime.getDate() + 1);
                        initialEndTime.setHours(7, 0, 0, 0); // Next day 7am
                    }
                    const newShift = {
                        startTime: moment(startCustom).format('HH:mm:ss'),
                        endTime: moment(initialEndTime).format('HH:mm:ss'),
                        date: moment(startCustom).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    };
                    const hasOverlap = checkForOverlap(newShift, shifts);

                    // Check for overlap for the current date's newShift before adding to shifts array
                    if (hasOverlap) {
                        // change start time to end time of last shift
                        const lastShift = shifts[shifts.length - 1] || { endTime: '07:00:00' };
                        initialEndTime.setHours(7, 0, 0, 0);
                        if (lastShift.endTime === '07:00:00') {
                            initialEndTime.setHours(initialEndTime.getHours() + 12);
                        }
                        let initialBlockDate = initialEndTime;
                        const hasDateSelected = visitDates.some((item) => item.date === moment(initialEndTime).format('MM-DD-YYYY'));
                        if (!hasDateSelected) {
                            initialBlockDate = new Date(`${usToIso(sortedInputDates[0] ?? '')}T00:00:00`);
                        }
                        const newShift2 = {
                            startTime: lastShift.endTime,
                            endTime: moment(initialEndTime).format('HH:mm:ss'),
                            date: moment(initialEndTime).format('MM-DD-YYYY'),
                            blockDate: moment(initialBlockDate).format('MM-DD-YYYY'),
                        };
                        shifts.push(newShift2);
                    } else {
                        shifts.push(newShift);
                    }
                    nextStartTime = initialEndTime;
                }

                let nextEndTime = new Date(nextStartTime);
                nextEndTime.setHours(nextStartTime.getHours() + 12);
                if (nextEndTime.getHours() === 19 && nextStartTime.getHours() === 7) {
                    if (startCustom.getHours() > 7 && startCustom.getHours() <= 19) {
                        nextStartTime.setDate(nextStartTime.getDate() + 1);
                    }
                }
                let nextShift: customShiftProps;
                if (startCustom.getHours() > 19) {
                    const hasDateSelected = visitDates.some((item) => item.date === moment(nextStartTime).format('MM-DD-YYYY'));
                    let initialBlockDate = nextStartTime;
                    if (!hasDateSelected) {
                        initialBlockDate = new Date(`${usToIso(values.dates[0] ?? '')}T00:00:00`);
                    }
                    nextShift = {
                        startTime: moment(nextStartTime).format('HH:mm:ss'),
                        endTime: moment(nextEndTime).format('HH:mm:ss'),
                        date: moment(nextStartTime).format('MM-DD-YYYY'),
                        blockDate: moment(initialBlockDate).format('MM-DD-YYYY'),
                    };
                } else {
                    nextShift = {
                        startTime: moment(nextStartTime).format('HH:mm:ss'),
                        endTime: moment(nextEndTime).format('HH:mm:ss'),
                        date: moment(nextStartTime).format('MM-DD-YYYY'),
                        blockDate: moment(startCustom).format('MM-DD-YYYY'),
                    };
                }
                shifts.push(nextShift);

                if (!isExactStart) {
                    nextStartTime = nextEndTime;
                    nextEndTime = new Date(nextStartTime);
                    nextEndTime.setHours(nextStartTime.getHours() + 12);
                    let newShift: customShiftProps;

                    if (startCustom.getHours() > 19) {
                        newShift = {
                            startTime: moment(nextStartTime).format('HH:mm:ss'),
                            endTime: moment(nextEndTime).format('HH:mm:ss'),
                            date: moment(nextStartTime).format('MM-DD-YYYY'),
                            blockDate: moment(nextStartTime).format('MM-DD-YYYY'),
                        };
                    } else {
                        const hasDateSelected = visitDates.some((item) => item.date === moment(initialEndTime).format('MM-DD-YYYY'));
                        let initialBlockDate = startCustom;
                        if (!hasDateSelected) {
                            initialBlockDate = new Date(`${usToIso(sortedInputDates[0] ?? '')}T00:00`);
                        }
                        newShift = {
                            startTime: moment(nextStartTime).format('HH:mm:ss'),
                            endTime: moment(nextEndTime).format('HH:mm:ss'),
                            date: moment(nextStartTime).format('MM-DD-YYYY'),
                            blockDate: moment(initialBlockDate).format('MM-DD-YYYY'),
                        };
                    }
                    if ((shifts[shifts.length - 1]?.endTime !== '07:00:00' && shifts[shifts.length - 2]?.endTime !== '19:00:00') || (startCustom.getHours() > 7 && startCustom.getHours() < 19)) {
                        shifts.push(newShift);
                    }
                }
            });
        }

        if (values.shift === 'Custom') {
            sortedInputDates.forEach((date) => {
                const visitStart = `${usToIso(date)}T${values.customShiftStartTime}`;
                const visitEnd = `${usToIso(date)}T${values.customShiftEndTime}`;
                const initialEndTime = new Date(visitStart);
                const startCustom = new Date(visitStart);
                const endCustom = new Date(visitEnd);

                // Guarding allows any custom-shift duration — under-12h is
                // permitted and just gets billed as a 12h shift downstream.
                // Only mobile-response jobs (maxShiftHours > 0) enforce an
                // upper bound. Compute in minutes so sub-hour or
                // same-start/end shifts (treated as a 24h shift via the
                // midnight rollover below) aren't rejected as 0.
                if (maxShiftHours) {
                    const startMin = startCustom.getHours() * 60 + startCustom.getMinutes();
                    const endMin = endCustom.getHours() * 60 + endCustom.getMinutes();
                    const durationMinutes = endMin <= startMin
                        ? ((24 * 60) - startMin) + endMin
                        : endMin - startMin;
                    if (durationMinutes / 60 > maxShiftHours) {
                        notification.error({
                            message: 'Invalid Shift Time',
                            description: `The shift you selected is more than ${maxShiftHours} hours. Please select a valid shift time to create a shift.`,
                        });
                        return;
                    }
                }

                // Create a single continuous shift without splitting at 7am/7pm
                const endTime = new Date(visitEnd);
                // If end time is before start time, it crosses midnight
                if (endTime <= startCustom) {
                    initialEndTime.setDate(initialEndTime.getDate() + 1);
                }
                initialEndTime.setHours(endCustom.getHours(), endCustom.getMinutes(), 0, 0);

                const singleShift = {
                    startTime: moment(startCustom).format('HH:mm:ss'),
                    endTime: moment(initialEndTime).format('HH:mm:ss'),
                    date: moment(startCustom).format('MM-DD-YYYY'),
                    blockDate: moment(startCustom).format('MM-DD-YYYY'),
                };
                const hasOverlap = checkForOverlap(singleShift, shifts);
                if (hasOverlap) {
                    notification.error({
                        message: 'Shift Overlap',
                        description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.',
                    });
                    return;
                }
                shifts.push(singleShift);
            });
        }
        if (values.timeSlot !== 'Custom' && sortedInputDates.length > 0 && values.shift !== 'Custom') {
            // Exclude the dates we're (re)saving from the overlap source —
            // otherwise a date being rewritten overlaps with its own existing
            // saved entry, raising a false "Shift Overlap" per date (e.g.
            // single-shift mode re-applies the shift to every date).
            const existingShiftsForOverlap = visitDates.filter((vd) => !sortedInputDates.includes(vd.date));
            let hasOverlap = false;
            sortedInputDates.forEach((date) => {
                const newShift = {
                    startTime: values.startTime || '',
                    endTime: values.endTime || '',
                    date,
                    blockDate: null,
                };

                if (checkForOverlap(newShift, existingShiftsForOverlap)) {
                    notification.error({ message: 'Shift Overlap', description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.' });
                    hasOverlap = true;
                }
            });
            if (hasOverlap) {
                return;
            }
        }

        // Same self-overlap guard: a date being rewritten can't overlap with
        // its own previous entry.
        const rewrittenDates = new Set([...sortedInputDates, ...shifts.map((s) => s.date)]);
        const existingShiftsForOverlap = visitDates.filter((vd) => !rewrittenDates.has(vd.date));
        const hasOverlap = shifts.some((shift) => checkForOverlap(shift, existingShiftsForOverlap));
        if (hasOverlap) {
            notification.error({ message: 'Shift Overlap', description: 'Your requested shift start/end time overlaps with an existing shift. Please enter a different start time.' });
            return;
        }

        const completeVisitDates = [...visitDates];

        // A retail 24/7 re-save can change how many blocks a date holds (a
        // cover start past the night start leaves one block, not two), so the
        // rows the new blocks no longer include are dropped rather than left
        // beside them. Rows without a time are the calendar placeholders the
        // merge below fills in.
        if (isRetail247) {
            const covered = new Set(shifts.map((shift) => shift.date));
            for (let index = completeVisitDates.length - 1; index >= 0; index -= 1) {
                const row = completeVisitDates[index];
                if (row?.startTime && covered.has(row.date)
                    && !shifts.some((shift) => shift.date === row.date && shift.startTime === row.startTime)) {
                    completeVisitDates.splice(index, 1);
                }
            }
        }

        const uniqueblockDateShifts = shifts.filter((shift, index, self) => index === self.findIndex((t) => t.blockDate === shift.blockDate));

        uniqueblockDateShifts.forEach(shiftToUpdate => {
            const matchingIndex = completeVisitDates.findIndex(visitDate => visitDate.date === shiftToUpdate.blockDate && !visitDate.blockDate);

            if (matchingIndex > -1) {
                completeVisitDates[matchingIndex] = {
                    ...completeVisitDates[matchingIndex], // Preserve existing properties
                    date: shiftToUpdate.date,
                    startTime: shiftToUpdate.startTime,
                    endTime: shiftToUpdate.endTime,
                    blockDate: shiftToUpdate.blockDate,
                    saved: completeVisitDates[matchingIndex]?.saved || false,
                    id: completeVisitDates[matchingIndex]?.id || null,
                };
            }
        });

        shifts.forEach((shift) => {
            if (!completeVisitDates.some(date => date.date === shift.date && date.startTime === shift.startTime)) {
                completeVisitDates.push({
                    startTime: shift.startTime,
                    endTime: shift.endTime,
                    startDate: shift.date,
                    endDate: shift.date,
                    date: shift.date,
                    saved: false,
                    id: null,
                    blockDate: shift.blockDate,
                });
            }
        });

        const updatedVisitDates = completeVisitDates.map((date) => {
            const customShift = shifts.find(shift => shift.date === date.date && shift.startTime === date.startTime);
            if (customShift) {
                return {
                    ...date,
                    saved: true,
                    shiftName: values.shift,
                    startTime: customShift.startTime,
                    endTime: customShift.endTime,
                    blockDate: customShift.blockDate,
                    shiftType: values.shiftType ? parseInt(values.shiftType, 10) : 0,
                };
            // A picked time is normally what marks a date as ready to save, but
            // retail has no time picker: its hours come from the site's shift
            // row for that weekday, resolved just below.
            } if (sortedInputDates.includes(date.date) && (date.startTime || values.startTime || isRetailResolved)) {
                // For retail sites with per-day shifts, look up the correct
                // shift for this date's day.
                let shiftStart = values.startTime || date.startTime;
                let shiftEnd = values.endTime || date.endTime;
                let shiftName = values.shift;
                let shiftType = values.shiftType ? parseInt(values.shiftType, 10) : 0;

                if (isRetail && stepData?.shifts) {
                    const resolved = resolveRetailShiftForDate(
                        stepData.shifts,
                        date.date,
                        values.shift ?? '',
                    );
                    if (resolved) {
                        shiftStart = resolved.startTime;
                        shiftEnd = resolved.endTime;
                        shiftName = resolved.shiftName;
                        shiftType = resolved.shiftType;
                    } else {
                        // Shift type not available for this day — skip
                        retailSkippedDates.push(
                            moment(date.date, 'MM-DD-YYYY').format('DD/MM/YYYY'),
                        );
                        return date;
                    }
                }

                return {
                    ...date,
                    saved: true,
                    shiftName,
                    startTime: shiftStart,
                    endTime: shiftEnd,
                    shiftType,
                };
            }
            return date;
        });

        if (retailSkippedDates.length > 0) {
            // Deduped: a 24/7 date the store is shut on is caught both when its
            // blocks are built and again when the day's shift row is looked up.
            const skipped = [...new Set(retailSkippedDates)];
            notification.error({
                message: 'Shift Not Available',
                description: `${values.shift} is not available for: ${skipped.join(', ')}.`,
            });
        }

        // A date booked on 24/7 holds TWO rows (its open and closed blocks), and
        // re-saving it onto a single-block shift rewrites both — so without this
        // the date renders twice with identical times.
        const collapsedVisitDates = collapseSingleBlockDates(updatedVisitDates, {
            rewrittenDates: sortedInputDates,
            shiftName: values.shift,
            blockBasedDates: new Set(
                shifts.filter((shift) => shift.blockDate).map((shift) => shift.blockDate),
            ),
        });

        // sort by date
        collapsedVisitDates.sort((a, b) => {
            const dateA = a.date || '';
            const dateB = b.date || '';

            // Compare dates
            if (dateA < dateB) {
                return -1;
            } if (dateA > dateB) {
                return 1;
            }
            return 0;
        });

        const newShifts: ShiftSelected[] = [];
        collapsedVisitDates.forEach((day, index) => {
            const dateString = day.date || '';
            const parts = dateString.split('-');
            const formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;

            if (day.saved && day.shiftName && day.startTime && day.endTime) {
                newShifts.push({
                    ...day,
                    id: day.id || null,
                    _tempIndex: index,
                    date: formattedDate,
                    day: dateString,
                    shift: day.shiftName,
                    shiftType: day.shiftType,
                    startTime: day.startTime,
                    endTime: day.endTime,
                    blockDate: day.blockDate,
                });
            }
        });

        // newShifts is already a complete mirror of the saved entries, so set it
        // outright. Appending to the previous list and deduping on
        // date+startTime kept stale rows alive: a resaved date whose start time
        // changed produced a different key, so the old shift survived alongside
        // the new one (Night 17:30 + Custom 15:00 on the same day) and that list
        // is what gets sent on Next.
        setSelectedShift(newShifts);

        setVisitDates(collapsedVisitDates);
    };

    function transformShiftData(shifts: Shift[], filterDayNums?: number[]) {
        // Retail keeps a slot per weekday: no slot is picked there, each date
        // resolves the one for its own day.
        return buildShiftOptions(shifts, filterDayNums, isRetail) as ShiftOption[];
    }

    const handleRemoveShift = (date: string) => {
        const dateToRemove = visitDates.find(visitDate => visitDate.date === date && visitDate.blockDate);
        const blockDateToRemove = dateToRemove?.blockDate;
        const updatedVisitDates = visitDates.map((visitDate) => {
            if (visitDate.date === date && !visitDate.blockDate) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                };
            } if (visitDate.blockDate === blockDateToRemove && blockDateToRemove !== undefined) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                    blockDate: visitDate.blockDate,
                };
            }
            return visitDate;
        });

        const blockDateExists = updatedVisitDates.some((visitDate) => visitDate.blockDate === blockDateToRemove);
        if (blockDateExists && blockDateToRemove !== undefined) {
            const uniqueblockDates = new Set();
            const filteredVisitDates = updatedVisitDates.filter(day => {
                if (!day.blockDate || !uniqueblockDates.has(day.blockDate) || day.blockDate !== blockDateToRemove) {
                    uniqueblockDates.add(day.blockDate);
                    return true;
                }
                return false;
            });
            const updatedblockDateDates = filteredVisitDates.map((visitDate) => {
                if (visitDate.blockDate === blockDateToRemove) {
                    return {
                        ...visitDate,
                        date: visitDate.blockDate || '',
                        blockDate: null,
                    };
                }
                return visitDate;
            });
            updatedblockDateDates.sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';
                if (dateA < dateB) {
                    return -1;
                } if (dateA > dateB) {
                    return 1;
                }
                return 0;
            });
            setVisitDates(updatedblockDateDates);
            setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(shift => shift.blockDate !== blockDateToRemove));
        } else {
            setVisitDates(updatedVisitDates);
        }
        // Matching only on `day` never matched a row hydrated from the API on
        // amend (those have no `day`), so removing or editing an already-saved
        // shift left it in the payload — swapping a Night shift for a Custom one
        // sent both and the request kept two shifts on the same date.
        setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(
            shift => !shiftIsOnCalendarDate(shift, date),
        ));
    };

    const handleEditShift = (date: string) => {
        const dateToRemove = visitDates.find(visitDate => visitDate.date === date && visitDate.blockDate);
        const blockDateToRemove = dateToRemove?.blockDate;
        const updatedVisitDates = visitDates.map((visitDate) => {
            if (visitDate.date === date && !visitDate.blockDate) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    shiftType: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                };
            } if (visitDate.blockDate === blockDateToRemove && blockDateToRemove !== undefined) {
                return {
                    ...visitDate,
                    saved: false,
                    shiftName: undefined,
                    shiftType: undefined,
                    startTime: undefined,
                    endTime: undefined,
                    range: false,
                    startDate: undefined,
                    endDate: undefined,
                    blockDate: visitDate.blockDate,
                    date: visitDate.blockDate || '',
                };
            }
            return visitDate;
        });

        const blockDateExists = updatedVisitDates.some((visitDate) => visitDate.blockDate === blockDateToRemove);
        if (blockDateExists && blockDateToRemove !== undefined) {
            const uniqueblockDates = new Set();
            const filteredVisitDates = updatedVisitDates.filter(day => {
                if (!day.blockDate || !uniqueblockDates.has(day.blockDate) || day.blockDate !== blockDateToRemove) {
                    uniqueblockDates.add(day.blockDate);
                    return true;
                }
                return false;
            });
            const updatedblockDateDates = filteredVisitDates.map((visitDate) => {
                if (visitDate.blockDate === blockDateToRemove) {
                    return {
                        ...visitDate,
                        blockDate: null,
                    };
                }
                return visitDate;
            });
            updatedblockDateDates.sort((a, b) => {
                const dateA = a.date || '';
                const dateB = b.date || '';
                if (dateA < dateB) {
                    return -1;
                } if (dateA > dateB) {
                    return 1;
                }
                return 0;
            });
            setVisitDates(updatedblockDateDates);
            setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(shift => shift.blockDate !== blockDateToRemove));
        } else {
            setVisitDates(updatedVisitDates);
        }
        // Matching only on `day` never matched a row hydrated from the API on
        // amend (those have no `day`), so removing or editing an already-saved
        // shift left it in the payload — swapping a Night shift for a Custom one
        // sent both and the request kept two shifts on the same date.
        setSelectedShift(prevSelectedShifts => prevSelectedShifts.filter(
            shift => !shiftIsOnCalendarDate(shift, date),
        ));
    };

    useEffect(() => {
        setSelectedShift(visitRequestState.dates || []);
        const dates = visitRequestState.dates?.reduce((acc: string[], curr) => {
            if (curr.blockDate === null) {
                acc.push(curr.date);
            } else {
                const alreadyIncluded = acc.some(date => {
                    const dateEntry = visitRequestState?.dates?.find(d => d.date === date);
                    return dateEntry?.blockDate === curr.blockDate && dateEntry?.date === curr.date;
                });
                if (!alreadyIncluded) {
                    acc.push(curr.date);
                }
            }
            return acc;
        }, []) || [];

        const allDate = visitRequestState.dates?.reduce((acc: {
            shift: string;
            id?: number;
            date:string;
            startTime: string;
            endTime: string;
            shiftType: string;
            blockDate?: string;}[], curr) => {
            if (curr.id === null || !acc.some(item => item.id === curr.id)) {
                acc.push(curr);
            }
            return acc;
        }, []) || [];
        const currentVisitDates = allDate.map((day) => {
            const date = formatDate(day.date);
            const parts = date.split('-');
            const formattedDate = `${parts[2]}-${parts[0]}-${parts[1]}`;
            const saved = visitRequestState.dates?.some((visitDate) => visitDate.date === day.date) || visitRequestState.dates?.some((visitDate) => visitDate.date === formattedDate) || false;

            return {
                id: day.id || null,
                date,
                saved,
                shiftName: day.shift,
                shiftType: day.shiftType,
                startTime: day.startTime,
                endTime: day.endTime,
                blockDate: day.blockDate,
            };
        });
        currentVisitDates.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        setSelectedDates(dates.map(date => formatDate(date)));
        setVisitDates(currentVisitDates);
    }, [visitRequestState.dates]);

    useEffect(() => {
        if (stepData?.shifts) {
            const dayFilter = getRetailDayFilter(visitDates, isRetail);
            const built = transformShiftData(stepData.shifts, dayFilter);
            // 24/7 is offered on retail from the store's opening times rather
            // than from a shift row, so no site carries a "Sunday 24/7" shift.
            // Retail guarding leads with the night shift; other site types
            // keep the order the site's shifts come back in.
            const options = isRetail
                ? orderRetailShiftOptions(
                    applyRetail247Option(built, openDaysByDay, dayFilter),
                ) as ShiftOption[]
                : built;
            // Mobile-response worktypes cap shifts at 3h, and the backend
            // only enforces that cap on Custom shifts — so hide every
            // predefined shift (retail and non-retail) to force the user
            // down the Custom path.
            setShiftOptions(isMobileResponse ? options.filter((o) => o.name === 'Custom') : options);
        }
    }, [stepData?.shifts, visitDates, isRetail, isMobileResponse, openDaysByDay]);

    useEffect(() => {
        if (stepData.costCode) {
            setCostCentre(stepData.costCode.costCentre || '');
            setCostApprovalEmail(stepData.costCode.costApprovalEmail || '');
        }
    }, [stepData.costCode]);

    const siteConfigError = stepData?.siteConfigError;

    return (
        <RequestFormLayout
            currentStep={isRetail ? 1 : 2}
            isNextEnabled={!siteConfigError}
            isBackEnabled={!isRetail}
            {...(isRetail ? {} : { onBackClick: handleBackClick })}
            onNextClick={handleNextClick}
            stepperLabels={isRetail ? GUARDING_STEPPER_LABELS_RETAIL : GUARDING_STEPPER_LABELS}
            formTitle="Guarding Request Form"
            reduxSliceKey="guardingRequestForm">
            {siteConfigError ? (
                <Alert
                    type="error"
                    showIcon
                    message="Site Configuration Required"
                    description={siteConfigError}
                    style={{ marginBottom: 24 }} />
            ) : (
                <>
                    <DateSelector
                        workTypeName={stepData?.workTypeName || visitRequestState.workTypeName}
                        minimumNoticeDays={minimumNoticeDays}
                        handleDatesSelect={handleDatesSelect}
                        selectedDates={selectedDates}
                        selectedShift={selectedShift}
                        holidays={holidays}
                        minDate={minimumCalendarDate} />
                    <ShiftSelector
                        visitDates={visitDates}
                        shiftOptions={shiftOptions}
                        handleRemoveShift={handleRemoveShift}
                        handleSaveShift={handleSaveShift}
                        handleEditShift={handleEditShift}
                        enabled24_7={stepData.enabled24_7}
                        totalHours={billedHours}
                        onChangetotalHours={(value) => setBilledHours(value)}
                        maxShiftHours={maxShiftHours}
                        isGuardingRequest
                        bankHolidays={bankHolidayList}
                        isRetail={isRetail}
                        openDaysByDay={openDaysByDay}
                        approvedVisits={stepData?.approvedVisits ?? []} />
                    <CostCode
                        code={costCentre}
                        email={costApprovalEmail}
                        requireEmail={requireEmail}
                        showInfo={false}
                        billedHours={billedHours}
                        onChangeCode={(value) => setCostCentre(value)}
                        onChangeEmail={(value) => setCostApprovalEmail(value)} />
                    <BankHolidayWarningModal
                        open={bankHolidayModal.open}
                        dates={bankHolidayCharges}
                        onBack={() => setBankHolidayModal({ open: false, navigationState: null })}
                        onContinue={() => {
                            const { navigationState } = bankHolidayModal;
                            setBankHolidayAcknowledged(true);
                            setBankHolidayModal({ open: false, navigationState: null });
                            // Straight on with the rest of handleNextClick: re-entering
                            // it would read the acknowledgement before React has set it
                            // and reopen the warning.
                            handleNextClick(navigationState ?? {}, true);
                        }} />
                </>
            )}
        </RequestFormLayout>
    );
};

export default GuardingDateSelectionForm;
